// Rate Editor — supervisor/admin live editor for the TAXI Calculator fare
// table + fuel baseline. Saving auto-bumps the pricing version server-side.
// Scoped (Tailwind utilities + shadcn Dialog). Never touches booking data.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

function Num({ label, value, onChange, step = "0.01", testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</Label>
      <Input
        type="number" step={step} value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-zinc-950 border-zinc-700 h-8 mt-1 text-sm"
      />
    </div>
  );
}

export default function RateEditorDialog({ open, onOpenChange, config, onSaved }) {
  const [g, setG] = useState({});           // global scalars
  const [fuel, setFuel] = useState({});     // profileKey -> coefficient
  const [dests, setDests] = useState([]);   // [{key, display, base_fare, miles, additional_passenger}]
  const [specials, setSpecials] = useState([]);  // Weatherford special-rate packages
  const [defaultKey, setDefaultKey] = useState("");  // geo-fence default suggestion
  const [addon, setAddon] = useState({});   // {child_seat_rate, satcom_wifi_rate, satcom_free_threshold, cc_surcharge_pct}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !config) return;
    setG({
      weatherford_base_fare: config.weatherford_base_fare,
      regional_additional_passenger: config.regional_additional_passenger,
      round_trip_multiplier: config.round_trip_multiplier,
      extra_stop_rate: config.extra_stop_rate,
      waiting_free_minutes: config.waiting_free_minutes,
      waiting_rate_per_minute: config.waiting_rate_per_minute,
      min_trip_duration_minutes: config.min_trip_duration_minutes,
      unlisted_ok_base: config.unlisted_ok?.base,
      unlisted_ok_per_mile: config.unlisted_ok?.per_mile,
      unlisted_ok_additional_passenger: config.unlisted_ok?.additional_passenger,
      fuel_baseline_price: config.fuel_baseline_price,
    });
    setFuel(Object.fromEntries((config.fuel_profiles || []).map((p) => [p.key, p.coefficient])));
    setDests((config.destinations || []).map((d) => ({
      key: d.key, display: d.display,
      base_fare: d.base_fare, miles: d.miles, additional_passenger: d.additional_passenger,
    })));
    setSpecials((config.special_rates || []).map((s) => ({
      key: s.key, display: s.display, per_rider: s.per_rider, booking_fee: s.booking_fee,
      min_riders: s.min_riders, max_riders: s.max_riders, enabled: s.enabled !== false,
    })));
    setDefaultKey(config.default_weatherford_key || "");
    setAddon({
      child_seat_rate: config.addons?.child_seat_rate,
      satcom_wifi_rate: config.addons?.satcom_wifi_rate,
      satcom_free_threshold: config.addons?.satcom_free_threshold,
      cc_surcharge_pct: config.cc_surcharge_pct,
    });
  }, [open, config]);

  const setGField = (k, v) => setG((s) => ({ ...s, [k]: v }));
  const setDestField = (key, f, v) => setDests((rows) => rows.map((r) => (r.key === key ? { ...r, [f]: v } : r)));
  const setSpecialField = (key, f, v) => setSpecials((rows) => rows.map((r) => (r.key === key ? { ...r, [f]: v } : r)));
  const setAddonField = (k, v) => setAddon((s) => ({ ...s, [k]: v }));

  const buildPayload = () => ({
    ...Object.fromEntries(Object.entries(g).map(([k, v]) => [k, v === "" || v === null ? null : Number(v)])),
    fuel_profiles: Object.fromEntries(Object.entries(fuel).map(([k, v]) => [k, { coefficient: Number(v) }])),
    destinations: dests.map((d) => ({
      key: d.key, base_fare: Number(d.base_fare), miles: Number(d.miles), additional_passenger: Number(d.additional_passenger),
    })),
    special_rates: specials.map((s) => ({
      key: s.key, per_rider: Number(s.per_rider), booking_fee: Number(s.booking_fee),
      min_riders: Number(s.min_riders), max_riders: Number(s.max_riders), enabled: !!s.enabled,
    })),
    default_weatherford_key: defaultKey || null,
    child_seat_rate: addon.child_seat_rate === "" || addon.child_seat_rate == null ? null : Number(addon.child_seat_rate),
    satcom_wifi_rate: addon.satcom_wifi_rate === "" || addon.satcom_wifi_rate == null ? null : Number(addon.satcom_wifi_rate),
    satcom_free_threshold: addon.satcom_free_threshold === "" || addon.satcom_free_threshold == null ? null : Number(addon.satcom_free_threshold),
    cc_surcharge_pct: addon.cc_surcharge_pct === "" || addon.cc_surcharge_pct == null ? null : Number(addon.cc_surcharge_pct),
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/taxi-calculator/rate-config", buildPayload());
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rates.");
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.post("/taxi-calculator/rate-config/reset");
      onSaved?.(data);
      onOpenChange(false);
      toast.success("Rate table restored to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset rates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="rate-editor-dialog"
        className="bg-zinc-950 border-indigo-400/40 text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-indigo-100">Edit Fare Rates</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Live rate table + fuel baseline. Saving auto-bumps the pricing version
            {config?.pricing_version ? ` (currently v${config.pricing_version})` : ""}. Does not affect any booking.
          </DialogDescription>
        </DialogHeader>

        {/* Global rates */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Num label="Weatherford flat fare" value={g.weatherford_base_fare ?? ""} onChange={(v) => setGField("weatherford_base_fare", v)} testid="rate-weatherford" />
          <Num label="Regional add'l passenger" value={g.regional_additional_passenger ?? ""} onChange={(v) => setGField("regional_additional_passenger", v)} testid="rate-regional-addl" />
          <Num label="Round-trip multiplier" value={g.round_trip_multiplier ?? ""} onChange={(v) => setGField("round_trip_multiplier", v)} testid="rate-roundtrip" />
          <Num label="Extra-stop rate" value={g.extra_stop_rate ?? ""} onChange={(v) => setGField("extra_stop_rate", v)} testid="rate-stop" />
          <Num label="Waiting free minutes" value={g.waiting_free_minutes ?? ""} onChange={(v) => setGField("waiting_free_minutes", v)} step="1" testid="rate-wait-free" />
          <Num label="Waiting $/min" value={g.waiting_rate_per_minute ?? ""} onChange={(v) => setGField("waiting_rate_per_minute", v)} testid="rate-wait-rate" />
          <Num label="Min trip minutes" value={g.min_trip_duration_minutes ?? ""} onChange={(v) => setGField("min_trip_duration_minutes", v)} step="1" testid="rate-min-trip" />
          <Num label="Fuel baseline $/gal" value={g.fuel_baseline_price ?? ""} onChange={(v) => setGField("fuel_baseline_price", v)} testid="rate-fuel-baseline" />
        </div>

        {/* Unlisted OK formula */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-1">Unlisted Oklahoma formula</div>
          <div className="grid grid-cols-3 gap-3">
            <Num label="Base" value={g.unlisted_ok_base ?? ""} onChange={(v) => setGField("unlisted_ok_base", v)} testid="rate-unlisted-base" />
            <Num label="Per mile" value={g.unlisted_ok_per_mile ?? ""} onChange={(v) => setGField("unlisted_ok_per_mile", v)} testid="rate-unlisted-permile" />
            <Num label="Add'l passenger" value={g.unlisted_ok_additional_passenger ?? ""} onChange={(v) => setGField("unlisted_ok_additional_passenger", v)} testid="rate-unlisted-addl" />
          </div>
        </div>

        {/* Fuel coefficients */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-1">Fuel coefficients ($/mi per $1 over baseline)</div>
          <div className="grid grid-cols-3 gap-3">
            {(config?.fuel_profiles || []).map((p) => (
              <Num key={p.key} label={p.label} value={fuel[p.key] ?? ""} onChange={(v) => setFuel((s) => ({ ...s, [p.key]: v }))} testid={`rate-fuel-${p.key}`} />
            ))}
          </div>
        </div>

        {/* Destination table */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-1">Destination fares</div>
          <div className="rounded border border-zinc-800 overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 px-2 py-1.5 bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500">
              <span>Destination</span><span>Base fare</span><span>Miles</span><span>Add'l pax</span>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-zinc-900">
              {dests.map((d) => (
                <div key={d.key} className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-2 px-2 py-1 items-center">
                  <span className="text-xs text-zinc-200 truncate" title={d.display}>{d.display}</span>
                  <Input type="number" step="0.01" value={d.base_fare} onChange={(e) => setDestField(d.key, "base_fare", e.target.value)} data-testid={`rate-dest-${d.key}-base`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <Input type="number" step="0.1" value={d.miles} onChange={(e) => setDestField(d.key, "miles", e.target.value)} data-testid={`rate-dest-${d.key}-miles`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <Input type="number" step="0.01" value={d.additional_passenger} onChange={(e) => setDestField(d.key, "additional_passenger", e.target.value)} data-testid={`rate-dest-${d.key}-addl`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Weatherford Special Rate Packages */}
        <div className="mt-2" data-testid="rate-editor-specials">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">Weatherford Special Rate Packages</div>
          <div className="rounded border border-zinc-800 overflow-hidden">
            <div className="grid grid-cols-[1.6fr_0.9fr_0.9fr_0.7fr_0.7fr_0.6fr] gap-2 px-2 py-1.5 bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500">
              <span>Package</span><span>Per-rider</span><span>Booking fee</span><span>Min</span><span>Max</span><span>On</span>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-zinc-900">
              {specials.map((s) => (
                <div key={s.key} className="grid grid-cols-[1.6fr_0.9fr_0.9fr_0.7fr_0.7fr_0.6fr] gap-2 px-2 py-1 items-center">
                  <span className="text-xs text-zinc-200 truncate" title={s.display}>{s.display}</span>
                  <Input type="number" step="0.01" value={s.per_rider} onChange={(e) => setSpecialField(s.key, "per_rider", e.target.value)} data-testid={`rate-special-${s.key}-perrider`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <Input type="number" step="0.01" value={s.booking_fee} onChange={(e) => setSpecialField(s.key, "booking_fee", e.target.value)} data-testid={`rate-special-${s.key}-booking`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <Input type="number" step="1" value={s.min_riders} onChange={(e) => setSpecialField(s.key, "min_riders", e.target.value)} data-testid={`rate-special-${s.key}-min`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <Input type="number" step="1" value={s.max_riders} onChange={(e) => setSpecialField(s.key, "max_riders", e.target.value)} data-testid={`rate-special-${s.key}-max`} className="bg-zinc-950 border-zinc-700 h-7 text-xs" />
                  <input type="checkbox" checked={!!s.enabled} onChange={(e) => setSpecialField(s.key, "enabled", e.target.checked)} data-testid={`rate-special-${s.key}-enabled`} className="h-4 w-4 accent-amber-400" />
                </div>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Max riders of 0 = no cap. Total = booking fee + per-rider × riders + add-ons.</div>
          <div className="mt-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Geo-fence default package (auto-suggested when pickup + destination are both inside Weatherford)</Label>
            <select
              value={defaultKey}
              onChange={(e) => setDefaultKey(e.target.value)}
              data-testid="rate-special-default"
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 h-8 mt-1 text-sm text-zinc-100"
            >
              <option value="">— none —</option>
              {specials.map((s) => (
                <option key={s.key} value={s.key}>{s.display}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Special-rate add-ons + card surcharge */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">Special-rate add-ons &amp; card surcharge</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Num label="Child seat ($/seat)" value={addon.child_seat_rate ?? ""} onChange={(v) => setAddonField("child_seat_rate", v)} testid="rate-addon-childseat" />
            <Num label="Satcom Wi-Fi ($/rider)" value={addon.satcom_wifi_rate ?? ""} onChange={(v) => setAddonField("satcom_wifi_rate", v)} testid="rate-addon-satcom" />
            <Num label="Wi-Fi free threshold ($)" value={addon.satcom_free_threshold ?? ""} onChange={(v) => setAddonField("satcom_free_threshold", v)} testid="rate-addon-threshold" />
            <Num label="Card surcharge (0.02 = 2%)" value={addon.cc_surcharge_pct ?? ""} step="0.005" onChange={(v) => setAddonField("cc_surcharge_pct", v)} testid="rate-addon-ccpct" />
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Surcharge applies to Mastercard / Discover / Amex only. Visa, cash, Cash App, Venmo = 0%.</div>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={restoreDefaults} disabled={saving} data-testid="rate-editor-reset" className="bg-zinc-950 border-zinc-700 text-xs">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Restore Defaults
          </Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="rate-editor-save" className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold text-xs">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save &amp; Bump Version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
